<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, ref, watch } from 'vue';
import LeasesAPI from 'dashboard/api/leases';
import { useAlert } from 'dashboard/composables';
import {
  defaultPayoutCompetency,
  defaultReadjustmentForm,
  formatDate,
  formatMoney,
  invoiceStatusMeta,
  leaseStatusMeta,
  payoutStatusMeta,
  pendingSeverityMeta,
} from '../utils/leaseOperation';

const props = defineProps({
  leaseId: {
    type: [String, Number],
    default: '',
  },
  title: {
    type: String,
    default: 'Operação da locação',
  },
});

const operation = ref(null);
const loading = ref(false);
const actionLoading = ref(false);
const loadError = ref('');
const preview = ref(null);
const readjustmentForm = ref(defaultReadjustmentForm(null));
const payoutCompetency = ref('');

const hasFinancials = computed(
  () => operation.value?.permissions?.financials_visible === true
);
const billing = computed(() => operation.value?.billing || {});
const delinquency = computed(() => operation.value?.delinquency || {});
const payout = computed(() => operation.value?.owner_payouts || {});
const readjustment = computed(() => operation.value?.readjustment || {});
const inspections = computed(() => operation.value?.inspections || {});
const maintenance = computed(() => operation.value?.maintenance || {});
const pendencies = computed(() => operation.value?.pendencies || []);
const history = computed(() => operation.value?.history || []);
const lease = computed(() => operation.value?.lease || {});
const nextCharge = computed(() => billing.value.next_charge);

const resetForms = () => {
  readjustmentForm.value = defaultReadjustmentForm(operation.value);
  payoutCompetency.value = defaultPayoutCompetency(operation.value);
  preview.value = null;
};

const fetchOperation = async () => {
  if (!props.leaseId) {
    operation.value = null;
    return;
  }

  loading.value = true;
  loadError.value = '';
  try {
    const { data } = await LeasesAPI.operation(props.leaseId);
    operation.value = data;
    resetForms();
  } catch (error) {
    operation.value = null;
    loadError.value =
      error.response?.data?.errors?.[0] ||
      error.response?.data?.message ||
      'Não foi possível carregar a operação da locação.';
    useAlert(loadError.value);
  } finally {
    loading.value = false;
  }
};

watch(() => props.leaseId, fetchOperation, { immediate: true });

const previewReadjustment = async () => {
  if (!props.leaseId) return;

  actionLoading.value = true;
  try {
    const { data } = await LeasesAPI.readjustmentPreview(
      props.leaseId,
      readjustmentForm.value
    );
    preview.value = data;
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] ||
        'Não foi possível gerar o preview do reajuste.'
    );
  } finally {
    actionLoading.value = false;
  }
};

const applyReadjustment = async () => {
  if (!props.leaseId || !preview.value?.preview_token) return;

  actionLoading.value = true;
  try {
    await LeasesAPI.readjust(props.leaseId, {
      ...readjustmentForm.value,
      preview_token: preview.value.preview_token,
    });
    useAlert('Reajuste aplicado com sucesso.');
    await fetchOperation();
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        error.response?.data?.errors?.[0] ||
        'Não foi possível aplicar o reajuste.'
    );
  } finally {
    actionLoading.value = false;
  }
};

const generateOwnerPayout = async () => {
  if (!props.leaseId || !payoutCompetency.value) return;

  actionLoading.value = true;
  try {
    await LeasesAPI.createOwnerPayout(props.leaseId, {
      competency: payoutCompetency.value,
      idempotency_key: `lease-${props.leaseId}-payout-${payoutCompetency.value}`,
    });
    useAlert('Repasse gerado com sucesso.');
    await fetchOperation();
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] || 'Não foi possível gerar o repasse.'
    );
  } finally {
    actionLoading.value = false;
  }
};
</script>

<template>
  <section class="grid gap-5">
    <div
      v-if="loading"
      class="rounded-lais border border-[#e5e7eb] bg-white p-6 text-[13px] text-[#718096]"
    >
      Carregando operação da locação...
    </div>

    <div
      v-else-if="loadError"
      class="rounded-lais border border-rose-100 bg-rose-50 p-5 text-[13px] text-rose-800"
    >
      <p class="font-semibold">Operação indisponível</p>
      <p class="mt-1">{{ loadError }}</p>
      <button
        type="button"
        class="mt-3 inline-flex h-9 items-center gap-2 rounded-lais bg-white px-3 text-[12px] font-semibold text-rose-700"
        @click="fetchOperation"
      >
        <span class="i-lucide-refresh-cw size-4" aria-hidden="true" />
        Tentar novamente
      </button>
    </div>

    <div
      v-else-if="!operation"
      class="rounded-lais border border-[#e5e7eb] bg-[#fafafa] p-6 text-[13px] text-[#718096]"
    >
      Selecione uma locação ativa para acompanhar cobranças, repasses, reajustes
      e vistorias.
    </div>

    <template v-else>
      <article
        class="rounded-lais border border-[#e5e7eb] bg-white p-5 shadow-lais-card"
      >
        <div class="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p
              class="text-[12px] font-semibold uppercase tracking-wide text-[#a0aec0]"
            >
              {{ title }}
            </p>
            <h2
              class="font-redHatDisplay mt-1 text-[24px] font-semibold text-[#1a1a1a]"
            >
              {{ lease.imovel_title || lease.reference }}
            </h2>
            <p class="mt-1 text-[13px] text-[#718096]">
              {{ lease.imovel_address || `Imóvel #${lease.imovel_id}` }}
            </p>
          </div>
          <span
            class="rounded-full px-3 py-1 text-[12px] font-semibold"
            :class="leaseStatusMeta(lease.status).className"
          >
            {{ leaseStatusMeta(lease.status).label }}
          </span>
        </div>

        <div class="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <section class="rounded-lais border border-[#eef1f6] p-4">
            <p class="text-[12px] font-semibold text-[#718096]">
              Próxima cobrança
            </p>
            <strong class="mt-2 block text-[20px] text-[#1a1a1a]">
              {{ formatMoney(nextCharge?.balance || nextCharge?.amount_due) }}
            </strong>
            <p class="mt-1 text-[12px] text-[#718096]">
              {{
                nextCharge
                  ? formatDate(nextCharge.due_date)
                  : 'Sem cobrança aberta'
              }}
            </p>
          </section>
          <section class="rounded-lais border border-[#eef1f6] p-4">
            <p class="text-[12px] font-semibold text-[#718096]">
              Inadimplência
            </p>
            <strong class="mt-2 block text-[20px] text-[#1a1a1a]">
              {{ formatMoney(delinquency.outstanding) }}
            </strong>
            <p class="mt-1 text-[12px] text-[#718096]">
              {{ delinquency.overdue_count || 0 }} vencida(s)
            </p>
          </section>
          <section class="rounded-lais border border-[#eef1f6] p-4">
            <p class="text-[12px] font-semibold text-[#718096]">
              Repasses pendentes
            </p>
            <strong class="mt-2 block text-[20px] text-[#1a1a1a]">
              {{ formatMoney(payout.totals?.pending_net) }}
            </strong>
            <p class="mt-1 text-[12px] text-[#718096]">
              {{ payout.pending_count || 0 }} pendente(s)
            </p>
          </section>
          <section class="rounded-lais border border-[#eef1f6] p-4">
            <p class="text-[12px] font-semibold text-[#718096]">Vistorias</p>
            <strong class="mt-2 block text-[20px] text-[#1a1a1a]">
              {{ inspections.items?.length || 0 }}
            </strong>
            <p class="mt-1 text-[12px] text-[#718096]">
              {{ inspections.pending_count || 0 }} pendente(s)
            </p>
          </section>
        </div>
      </article>

      <div
        v-if="!hasFinancials"
        class="rounded-lais border border-amber-100 bg-amber-50 p-4 text-[13px] text-amber-800"
      >
        Dados financeiros de cobrança, inadimplência, reajuste e repasse exigem
        permissão de financeiro.
      </div>

      <section v-if="pendencies.length" class="grid gap-3">
        <article
          v-for="pending in pendencies"
          :key="`${pending.kind}-${pending.title}-${pending.due_at}`"
          class="flex items-start gap-3 rounded-lais border p-4 text-[13px]"
          :class="pendingSeverityMeta(pending.severity).className"
        >
          <span
            :class="`${pendingSeverityMeta(pending.severity).icon} mt-0.5 size-4 shrink-0`"
            aria-hidden="true"
          />
          <div>
            <p class="font-semibold">{{ pending.title }}</p>
            <p class="mt-1 opacity-80">
              {{ pending.kind }} · {{ pending.status }}
              <template v-if="pending.due_at">
                · {{ formatDate(pending.due_at) }}
              </template>
            </p>
          </div>
        </article>
      </section>

      <div
        class="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]"
      >
        <article
          class="overflow-hidden rounded-lais border border-[#e5e7eb] bg-white shadow-lais-card"
        >
          <header class="border-b border-[#eef1f6] p-5">
            <h3
              class="font-redHatDisplay text-[20px] font-semibold text-[#1a1a1a]"
            >
              Cobranças e vencimentos
            </h3>
          </header>
          <div
            v-if="!billing.charges?.length"
            class="p-5 text-[13px] text-[#718096]"
          >
            Nenhuma cobrança lançada para esta locação.
          </div>
          <div v-else class="overflow-x-auto">
            <table class="w-full text-[13px]">
              <thead>
                <tr
                  class="border-b border-[#eef1f6] bg-[#fafafa] text-[11px] uppercase text-[#718096]"
                >
                  <th class="px-4 py-3 text-left">Competência</th>
                  <th class="px-4 py-3 text-left">Vencimento</th>
                  <th class="px-4 py-3 text-right">Valor</th>
                  <th class="px-4 py-3 text-right">Saldo</th>
                  <th class="px-4 py-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="charge in billing.charges"
                  :key="charge.id"
                  class="border-b border-[#f3f4f6]"
                >
                  <td class="px-4 py-3 font-semibold text-[#1a1a1a]">
                    {{ charge.competency }}
                  </td>
                  <td class="px-4 py-3 text-[#4a5568]">
                    {{ formatDate(charge.due_date) }}
                  </td>
                  <td class="px-4 py-3 text-right text-[#4a5568]">
                    {{ formatMoney(charge.amount_due) }}
                  </td>
                  <td class="px-4 py-3 text-right font-semibold text-[#1a1a1a]">
                    {{ formatMoney(charge.balance) }}
                  </td>
                  <td class="px-4 py-3 text-center">
                    <span
                      class="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                      :class="invoiceStatusMeta(charge.status).className"
                    >
                      {{ invoiceStatusMeta(charge.status).label }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </article>

        <article
          class="rounded-lais border border-[#e5e7eb] bg-white p-5 shadow-lais-card"
        >
          <h3
            class="font-redHatDisplay text-[20px] font-semibold text-[#1a1a1a]"
          >
            Reajuste
          </h3>
          <p class="mt-1 text-[13px] text-[#718096]">
            Critério {{ readjustment.criterion || '-' }} · próximo ciclo
            {{ readjustment.next_effective_competency || '-' }}
          </p>
          <div class="mt-4 grid gap-3">
            <label class="grid gap-1 text-[12px] text-[#718096]">
              Critério
              <select
                v-model="readjustmentForm.applied_index"
                class="rounded-lais border border-[#d6dce5] px-3 py-2 text-[13px] text-[#1a1a1a]"
                :disabled="!hasFinancials"
              >
                <option value="IGPM">IGP-M</option>
                <option value="IPCA">IPCA</option>
                <option value="INPC">INPC</option>
                <option value="IGP-DI">IGP-DI</option>
                <option value="IVAR">IVAR</option>
                <option value="none">Sem índice</option>
              </select>
            </label>
            <div class="grid gap-3 sm:grid-cols-2">
              <label class="grid gap-1 text-[12px] text-[#718096]">
                Competência
                <input
                  v-model="readjustmentForm.effective_competency"
                  class="rounded-lais border border-[#d6dce5] px-3 py-2 text-[13px] text-[#1a1a1a]"
                  placeholder="2027-01"
                  :disabled="!hasFinancials"
                />
              </label>
              <label class="grid gap-1 text-[12px] text-[#718096]">
                Fator
                <input
                  v-model="readjustmentForm.index_factor"
                  class="rounded-lais border border-[#d6dce5] px-3 py-2 text-[13px] text-[#1a1a1a]"
                  placeholder="1.0750"
                  :disabled="!hasFinancials"
                />
              </label>
            </div>
            <button
              type="button"
              class="inline-flex h-10 items-center justify-center gap-2 rounded-lais bg-[#49317a] px-4 text-[13px] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#cbd5e0]"
              :disabled="!hasFinancials || actionLoading"
              @click="previewReadjustment"
            >
              <span class="i-lucide-calculator size-4" aria-hidden="true" />
              Gerar preview
            </button>
          </div>

          <div
            v-if="preview"
            class="mt-4 rounded-lais border border-[#d8ecdf] bg-[#f4fbf6] p-4 text-[13px]"
          >
            <p class="font-semibold text-[#166534]">
              {{ formatMoney(preview.previous_rent) }} →
              {{ formatMoney(preview.new_rent) }}
            </p>
            <p class="mt-1 text-[#3f5f46]">
              Total mensal projetado:
              {{ formatMoney(preview.previous_total) }} →
              {{ formatMoney(preview.new_total) }}
            </p>
            <p class="mt-1 text-[#3f5f46]">
              {{ preview.target_competencies?.length || 0 }} competência(s)
              futuras a partir de {{ preview.effective_competency }}.
            </p>
            <button
              type="button"
              class="mt-3 inline-flex h-9 items-center justify-center rounded-lais bg-emerald-700 px-3 text-[12px] font-semibold text-white"
              :disabled="actionLoading"
              @click="applyReadjustment"
            >
              Aplicar reajuste
            </button>
          </div>
        </article>
      </div>

      <div class="grid gap-5 xl:grid-cols-2">
        <article
          class="rounded-lais border border-[#e5e7eb] bg-white p-5 shadow-lais-card"
        >
          <div class="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h3
                class="font-redHatDisplay text-[20px] font-semibold text-[#1a1a1a]"
              >
                Repasses ao proprietário
              </h3>
              <p class="mt-1 text-[13px] text-[#718096]">
                Valor líquido, status e divergências por competência.
              </p>
            </div>
            <div class="flex gap-2">
              <input
                v-model="payoutCompetency"
                class="h-9 w-28 rounded-lais border border-[#d6dce5] px-3 text-[13px]"
                placeholder="2026-01"
                :disabled="!hasFinancials"
              />
              <button
                type="button"
                class="inline-flex h-9 items-center rounded-lais bg-[#49317a] px-3 text-[12px] font-semibold text-white disabled:bg-[#cbd5e0]"
                :disabled="!hasFinancials || !payoutCompetency || actionLoading"
                @click="generateOwnerPayout"
              >
                Gerar
              </button>
            </div>
          </div>

          <div
            v-if="!payout.items?.length"
            class="mt-5 text-[13px] text-[#718096]"
          >
            Nenhum repasse registrado.
          </div>
          <div v-else class="mt-5 grid gap-3">
            <section
              v-for="item in payout.items"
              :key="item.id"
              class="rounded-lais border border-[#eef1f6] p-4"
            >
              <div class="flex items-start justify-between gap-3">
                <div>
                  <p class="font-semibold text-[#1a1a1a]">
                    {{ item.competency }} ·
                    {{ item.owner_name || 'Proprietário' }}
                  </p>
                  <p class="mt-1 text-[12px] text-[#718096]">
                    Líquido {{ formatMoney(item.net_amount) }} · bruto
                    {{ formatMoney(item.gross_amount) }}
                  </p>
                  <p
                    v-if="item.divergence"
                    class="mt-1 text-[12px] font-semibold text-rose-700"
                  >
                    Divergência entre recebido e repassado
                  </p>
                </div>
                <span
                  class="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                  :class="payoutStatusMeta(item.status).className"
                >
                  {{ payoutStatusMeta(item.status).label }}
                </span>
              </div>
            </section>
          </div>
        </article>

        <article
          class="rounded-lais border border-[#e5e7eb] bg-white p-5 shadow-lais-card"
        >
          <h3
            class="font-redHatDisplay text-[20px] font-semibold text-[#1a1a1a]"
          >
            Vistorias e pendências operacionais
          </h3>
          <div class="mt-4 grid gap-3">
            <section
              v-for="inspection in inspections.items || []"
              :key="inspection.id"
              class="rounded-lais border border-[#eef1f6] p-4"
            >
              <p class="font-semibold text-[#1a1a1a]">
                Vistoria de {{ inspection.kind }}
              </p>
              <p class="mt-1 text-[12px] text-[#718096]">
                {{ inspection.acceptance_status }} ·
                {{ formatDate(inspection.inspected_at) }} ·
                {{ inspection.divergence_count }} divergência(s)
              </p>
            </section>
            <p
              v-if="!(inspections.items || []).length"
              class="text-[13px] text-[#718096]"
            >
              Nenhuma vistoria registrada.
            </p>
          </div>

          <div class="mt-5 border-t border-[#eef1f6] pt-4">
            <p class="text-[13px] font-semibold text-[#1a1a1a]">
              Manutenção aberta: {{ maintenance.open_count || 0 }}
            </p>
            <div class="mt-3 grid gap-2">
              <p
                v-for="item in maintenance.items || []"
                :key="item.id"
                class="rounded-lais bg-[#fafafa] px-3 py-2 text-[12px] text-[#4a5568]"
              >
                {{ item.title }} · {{ item.status }} · {{ item.urgency }}
              </p>
            </div>
          </div>
        </article>
      </div>

      <article
        class="rounded-lais border border-[#e5e7eb] bg-white p-5 shadow-lais-card"
      >
        <h3 class="font-redHatDisplay text-[20px] font-semibold text-[#1a1a1a]">
          Histórico
        </h3>
        <ol class="mt-4 grid gap-3 text-[13px]">
          <li
            v-for="event in history"
            :key="`${event.kind}-${event.source_id}-${event.occurred_at}`"
            class="rounded-lais border border-[#eef1f6] px-4 py-3"
          >
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p class="font-semibold text-[#1a1a1a]">{{ event.title }}</p>
                <p class="mt-1 text-[#718096]">
                  {{ event.kind }} · {{ event.status }}
                  <template v-if="event.description">
                    · {{ event.description }}
                  </template>
                </p>
              </div>
              <span class="text-[12px] text-[#a0aec0]">
                {{ formatDate(event.occurred_at) }}
              </span>
            </div>
          </li>
        </ol>
      </article>
    </template>
  </section>
</template>
